import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import type { Notification } from '../../lib/types';
import { useLanguage } from '../../lib/i18n';

export function NotificationBell({ items, unread }: { items: Notification[]; unread: number }) {
  const { language } = useLanguage();
  const en = language === 'en';
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const markAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="notification-button"
        aria-expanded={open}
        aria-label={unread > 0
          ? (en ? `${unread} unread notifications` : `${unread} okunmamış bildirim`)
          : (en ? 'Notifications' : 'Bildirimler')}
        title={en ? 'Notifications' : 'Bildirimler'}
        onClick={() => setOpen((value) => !value)}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
        </svg>
        {unread > 0 ? <span className="notification-button__dot" aria-hidden="true" /> : null}
      </button>

      {open ? (
        <div
          className="card"
          style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 380, zIndex: 20, maxHeight: 460, overflowY: 'auto' }}
        >
          <div className="card__head">
            <h3 className="card__title">{en ? 'Notifications' : 'Bildirimler'}</h3>
            <button
              type="button"
              className="button button--ghost"
              disabled={unread === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              {en ? 'Mark all as read' : 'Tümünü okundu işaretle'}
            </button>
          </div>

          {items.length === 0 ? (
            <p className="empty">{en ? 'No notifications.' : 'Bildirim yok.'}</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
              {items.map((item) => (
                <li key={item.id} style={{ opacity: item.is_read ? 0.6 : 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span className={`badge badge--${severityClass(item.severity)}`}>
                      {severityLabel(item.severity, item.threshold_days, en)}
                    </span>
                    <strong style={{ fontSize: 13 }}>{item.title}</strong>
                  </div>
                  {item.body ? <div className="muted" style={{ fontSize: 12.5 }}>{item.body}</div> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function severityClass(severity: Notification['severity']): string {
  return severity === 'critical' ? 'critical' : severity === 'warning' ? 'warning' : 'upcoming';
}

/** The badge says what it means, so severity is never colour-alone. */
function severityLabel(severity: Notification['severity'], days: number | null, en: boolean): string {
  if (days !== null && days < 0) return en ? `${Math.abs(days)} days overdue` : `${Math.abs(days)} gün gecikti`;
  if (days === 0) return en ? 'Today' : 'Bugün';
  if (days !== null) return en ? `${days} days left` : `${days} gün kaldı`;
  return severity === 'critical' ? (en ? 'Critical' : 'Kritik') : severity === 'warning' ? (en ? 'Warning' : 'Uyarı') : (en ? 'Info' : 'Bilgi');
}
